import React from "react";
import { ArrowLeft } from "lucide-react";
import { LogoLockup } from "@/components/Logo";

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24 mb-10">
    <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-slate-50 mb-4">
      {title}
    </h2>
    <div className="text-slate-300 leading-relaxed space-y-4 text-[15px] md:text-base">
      {children}
    </div>
  </section>
);

const Terms = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-slate-950/80 border-b border-slate-800/60">
        <div className="max-w-4xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <span className="hidden sm:block">
              <LogoLockup size={36} />
            </span>
            <span className="sm:hidden">
              <LogoLockup size={28} />
            </span>
          </a>
          <a
            href="/"
            className="inline-flex items-center gap-2 text-slate-300 hover:text-cyan-300 transition-colors text-sm"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to site
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="mb-14 max-w-3xl">
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-cyan-400 mb-4">
            Legal
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight leading-[0.98] mb-5 text-balance">
            Terms of Service
          </h1>
          <p className="text-slate-400 text-base md:text-lg leading-relaxed">
            These Terms of Service ("Terms") govern your access to and use of
            the website and services provided by Cleveland Creative Group
            ("Company," "we," "us," or "our"). By using our website or
            services, you agree to be bound by these Terms.
          </p>
          <p className="text-slate-500 text-sm mt-4">
            Last updated:{" "}
            {new Date().toLocaleDateString(undefined, {
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </p>
        </div>

        <Section id="use" title="Use of Our Services">
          <p>
            You agree to use our website and services only for lawful purposes
            and in accordance with these Terms. You agree not to use our
            services in any way that:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              Violates any applicable federal, state, or local law or
              regulation
            </li>
            <li>
              Infringes the rights of, or restricts or inhibits use of our
              services by, any third party
            </li>
            <li>
              Introduces malicious code, attempts unauthorized access, or
              otherwise interferes with the operation of our systems
            </li>
            <li>
              Misrepresents your identity or impersonates any person or entity
            </li>
          </ul>
        </Section>

        <Section id="accounts" title="Accounts and Communications">
          <p>
            When you submit information to us through our website (including
            contact forms, payment forms, or the SMS opt-in form), you
            represent that the information you provide is accurate, current,
            and complete. You are responsible for maintaining the
            confidentiality of any credentials you use to access our services.
          </p>
          <p>
            By providing your phone number or email address, you agree that we
            may contact you using those channels for the purposes described in
            our{" "}
            <a
              href="/privacy"
              className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2"
            >
              Privacy Policy
            </a>{" "}
            and{" "}
            <a
              href="/sms-terms"
              className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2"
            >
              SMS Terms &amp; Consent
            </a>{" "}
            page.
          </p>
        </Section>

        <Section id="ip" title="Intellectual Property">
          <p>
            All content on our website — including text, graphics, logos, and
            software — is owned by Cleveland Creative Group or our licensors
            and is protected by intellectual property laws. You may not
            reproduce, distribute, or create derivative works without our
            prior written permission, except as expressly permitted by these
            Terms or applicable law.
          </p>
        </Section>

        <Section id="disclaimer" title="Disclaimer of Warranties">
          <p>
            Our website and services are provided on an "as is" and "as
            available" basis, without warranties of any kind, either express
            or implied. To the maximum extent permitted by law, we disclaim
            all warranties, including merchantability, fitness for a
            particular purpose, and non-infringement.
          </p>
        </Section>

        <Section id="liability" title="Limitation of Liability">
          <p>
            To the maximum extent permitted by law, Cleveland Creative Group
            and its affiliates, officers, employees, and agents shall not be
            liable for any indirect, incidental, special, consequential, or
            punitive damages arising out of or related to your use of our
            website or services.
          </p>
        </Section>

        <Section id="changes" title="Changes to These Terms">
          <p>
            We may update these Terms from time to time. When we do, we will
            revise the "Last updated" date at the top of this page. Continued
            use of our website or services after changes are posted
            constitutes acceptance of the revised Terms.
          </p>
        </Section>

        <Section id="governing-law" title="Governing Law">
          <p>
            These Terms are governed by the laws of the State of Ohio, without
            regard to its conflict-of-law principles. You agree to the
            exclusive jurisdiction of the state and federal courts located in
            Cuyahoga County, Ohio, for any disputes arising out of or related
            to these Terms or your use of our services.
          </p>
        </Section>

        <Section id="contact" title="Contact Us">
          <p>For questions about these Terms, please contact us at:</p>
          <div className="mt-3 rounded-xl border border-slate-800 bg-slate-900/60 p-5 text-sm">
            <div className="text-slate-100 font-semibold">
              Cleveland Creative Group
            </div>
            <div className="text-slate-400">Cleveland, Ohio</div>
            <div className="text-slate-400 mt-1">
              Email:{" "}
              <a
                href="mailto:jamursch@gmail.com"
                className="text-cyan-300 hover:text-cyan-200"
              >
                jamursch@gmail.com
              </a>
            </div>
          </div>
        </Section>

        <p className="mt-12 text-xs text-slate-500 italic">
          These Terms of Service are provided as a starting template for
          carrier review. They are not legal advice. Please have them reviewed
          by counsel familiar with your jurisdiction and business practices
          before relying on them.
        </p>
      </main>

      <footer className="border-t border-slate-900 py-10">
        <div className="max-w-4xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="text-xs text-slate-500 font-mono">
            © {new Date().getFullYear()} Cleveland Creative Group · Terms of
            Service
          </div>
          <div className="flex gap-5 text-xs">
            <a href="/sms-terms" className="text-slate-400 hover:text-cyan-300">
              SMS Terms
            </a>
            <a href="/privacy" className="text-slate-400 hover:text-cyan-300">
              Privacy Policy
            </a>
            <a href="/" className="text-slate-400 hover:text-cyan-300">
              Back to site
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Terms;
